import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useApplicationStore from '../../store/applicationStore';
import { CREDIT_API_URL } from '../../Constants';
import showToast from '../../utility/showToast';
import ConfirmationModal from '../../utility/ConfirmationModal';
import BootstrapDatePicker from '../../utility/BootstrapDatePicker';
import AutocompleteDropdown from '../../utility/AutocompleteDropdown';
import useAutocomplete from '../../hooks/useAutocomplete';

const ProfessionalDetailsStep = ({ onNext, onPrev, onEditStep, isSubmitting = false }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  
  // Pincode autocomplete using reusable hook for Office Address
  const workPincodeAutocomplete = useAutocomplete({
    apiEndpoint: '/pincode/search',
    minChars: 2,
    debounceDelay: 200,
    onSelect: (item) => {
      const pincodeValue = item.pinCode || item;
      const currentWorkAddress = professionalDetails.workAddress || {};
      const updatedWorkAddress = {
        ...currentWorkAddress,
        pincode: pincodeValue
      };
      updateProfessionalDetail('workAddress', updatedWorkAddress);
      
      // Clear error when user selects
      if (errors.professional['workAddress.pincode']) {
        setErrors('professional', { ...errors.professional, 'workAddress.pincode': '' });
      }
      
      // Validate the field
      const error = validateField('workAddress.pincode', pincodeValue);
      if (error) {
        setErrors('professional', { ...errors.professional, 'workAddress.pincode': error });
      }
    },
    getValue: (item) => item.pinCode || item,
    getKey: (item, index) => item.pinCode || index
  });
  
  // Zustand store
  const {
    professionalDetails,
    errors,
    leadData,
    updateProfessionalDetail,
    setProfessionalDetails,
    setErrors,
    isProfessionalDetailsValid,
    resetProfessionalDetails
  } = useApplicationStore();

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };



  const validatePinCode = (pinCode) => {
    const pinRegex = /^[0-9]{6}$/;
    return pinRegex.test(pinCode);
  };

  const validateIncome = (income) => {
    const numIncome = parseInt(income);
    return !isNaN(numIncome) && numIncome >= 5000;
  };

  const validateEmploymentType = (type) => {
    const validTypes = ['Salaried', 'SelfEmployed', 'Business', 'Other'];
    return validTypes.includes(type);
  };

  const validateSalaryReceivedType = (type) => {
    const validTypes = ['Direct Bank Account', 'Cheque', 'Cash'];
    return validTypes.includes(type);
  };

  const validateWorkSector = (sector) => {
    const validSectors = [
      'Agriculture',
      'Banking and Financial Services',
      'Construction, Real Estate and Infrastructure',
      'Education and Training',
      'Government and Public Administration',
      'Healthcare',
      'Information Technology',
      'Legal',
      'Manufacturing',
      'Media, Sports and Entertainment',
      'Oil and Gases',
      'Security and Law Enforcement',
      'Trade and Commerce',
      'Transport and Logistics',
      'Tourism and Hospitality',
      'Telecommunication',
      'Others'
    ];
    return validSectors.includes(sector);
  };

  const validateOrganization = (organization) => {
    const validOrganizations = [
      'Private Company',
      'Public Company',
      'Listed Public Company',
      'State Government',
      'Central Government',
      'Partnership Firm',
      'Proprietorship Firm',
      'Limited Liability Partnership (LLP)',
      'Trust'
    ];
    return validOrganizations.includes(organization);
  };

  const validateJobFunction = (jobFunction) => {
    const validJobFunctions = [
      'Research and development (R & D)',
      'Administration',
      'Customer service',
      'Sales',
      'Marketing',
      'IT and Engineering',
      'Human resources',
      'Finance',
      'Operations',
      'Legal and Audit',
      'Others'
    ];
    return validJobFunctions.includes(jobFunction);
  };

  // API Functions
  const loadProfessionalDetails = async () => {
    if (!leadData?.id) {
      console.log('No lead ID available for loading professional details');
      return;
    }

    setIsLoading(true);
    
    // Clear existing professional details to ensure fresh data from API
    resetProfessionalDetails();
    
    try {
      const response = await fetch(`${CREDIT_API_URL}/professional-details?leadId=${leadData.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Professional Details API Response:', result);
        
        if (result.status === 'Success' && result.data) {
          const professionalData = result.data;
          console.log('Processing professional data:', professionalData);
          
          // Pre-fill form with existing data - updated to match API structure
          const formData = {
            employmentType: professionalData.employmentType || '',
            companyName: professionalData.companyName || '',
            jobFunction: professionalData.jobFunction || '',
            designation: professionalData.designation || '',
            workSector: professionalData.workSector || '',
            organization: professionalData.organizationType || '',
            workingSince: professionalData.workingSince ? professionalData.workingSince.split('T')[0] : '', // Convert to YYYY-MM-DD format
            officialEmail: professionalData.officialEmail || '',
            salaryReceivedType: professionalData.salaryReceivedType || '',
            netMonthlyIncome: professionalData.netMonthlyIncome || '',
            workAddress: professionalData.workAddress || {
              address: '',
              locality: '',
              pincode: '',
              city: '',
              state: ''
            }
          };
          
          console.log('Setting professional details from API:', formData);
          setProfessionalDetails(formData);
          
          // Clear any existing errors
          setErrors('professional', {});
          
          // Mark that data has been loaded
          setHasLoadedData(true);
        } else {
          console.log('No professional details found for this lead');
          setHasLoadedData(false);
        }
      } else {
        console.error('Failed to load professional details:', response.status);
        setHasLoadedData(false);
      }
    } catch (error) {
      console.error('Error loading professional details:', error);
      setHasLoadedData(false);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProfessionalDetails = async () => {
    if (!leadData?.id) {
      throw new Error('No lead ID available');
    }

    const payload = {
      leadId: leadData.id,
      customerId: leadData.customerId || null, // Include customerId if available
      employmentType: professionalDetails.employmentType,
      companyName: professionalDetails.companyName,
      jobFunction: professionalDetails.jobFunction,
      designation: professionalDetails.designation,
      workSector: professionalDetails.workSector,
      organizationType: professionalDetails.organization,
      workingSince: professionalDetails.workingSince,
      officialEmail: professionalDetails.officialEmail,
      salaryReceivedType: professionalDetails.salaryReceivedType,
      netMonthlyIncome: professionalDetails.netMonthlyIncome,
      workAddress: professionalDetails.workAddress || {
        address: '',
        locality: '',
        pincode: '',
        city: '',
        state: ''
      }
    };

    console.log('Saving professional details with payload:', payload);
    
    const response = await fetch(`${CREDIT_API_URL}/professional-details`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    console.log('Professional details save response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Professional details save error:', errorData);
      throw new Error(errorData.message || 'Failed to save professional details');
    }

    const result = await response.json();
    console.log('Professional details save success:', result);
    return result;
  };

  // Load professional details on component mount
  useEffect(() => {
    if (leadData?.id) {
      loadProfessionalDetails();
    }
  }, [leadData?.id]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle workAddress nested fields
    if (name.startsWith('workAddress.')) {
      const field = name.split('.')[1]; // Get the field name after 'workAddress.'
      const currentWorkAddress = professionalDetails.workAddress || {};
      const updatedWorkAddress = {
        ...currentWorkAddress,
        [field]: value
      };
      updateProfessionalDetail('workAddress', updatedWorkAddress);
    } else {
      updateProfessionalDetail(name, value);
    }
    
    // Clear error when user starts typing
    if (errors.professional[name]) {
      setErrors('professional', { ...errors.professional, [name]: '' });
    }

    // Real-time validation for each field
    const error = validateField(name, value);
    if (error) {
      setErrors('professional', { ...errors.professional, [name]: error });
    }
  };

  // Handle PIN code change with validation
  const handlePinCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    const currentWorkAddress = professionalDetails.workAddress || {};
    const updatedWorkAddress = {
      ...currentWorkAddress,
      pincode: value
    };
    updateProfessionalDetail('workAddress', updatedWorkAddress);
    
    // Clear error when user starts typing
    if (errors.professional['workAddress.pincode']) {
      setErrors('professional', { ...errors.professional, 'workAddress.pincode': '' });
    }
  };

  // Validate individual field
  const validateField = (name, value) => {
    switch (name) {
      case 'employmentType':
        if (!value) {
          return 'Employment type is required';
        }
        if (!validateEmploymentType(value)) {
          return 'Please select a valid employment type';
        }
        break;
      case 'companyName':
        if (!value || value.trim().length === 0) {
          return 'Company name is required';
        }
        break;
      case 'jobFunction':
        if (!value || value.trim().length === 0) {
          return 'Job function is required';
        }
        if (!validateJobFunction(value)) {
          return 'Please select a valid job function';
        }
        break;
      case 'designation':
        if (!value || value.trim().length === 0) {
          return 'Designation is required';
        }
        break;
      case 'workSector':
        if (!value) {
          return 'Work sector is required';
        }
        if (!validateWorkSector(value)) {
          return 'Please select a valid work sector';
        }
        break;
      case 'organization':
        if (!value) {
          return 'Organization type is required';
        }
        if (!validateOrganization(value)) {
          return 'Please select a valid organization type';
        }
        break;
      case 'workingSince':
        if (!value) {
          return 'Working since date is required';
        }
        break;
      case 'officialEmail':
        if (value && !validateEmail(value)) {
          return 'Please enter a valid email address';
        }
        break;
      case 'salaryReceivedType':
        if (!value) {
          return 'Salary received type is required';
        }
        if (!validateSalaryReceivedType(value)) {
          return 'Please select a valid salary received type';
        }
        break;
      case 'netMonthlyIncome':
        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
          return 'Net monthly income is required';
        }
        if (!validateIncome(value)) {
          return 'Net monthly income must be at least ₹5,000';
        }
        break;
      case 'workAddress.pincode':
        if (!value) {
          return 'Work PIN code is required';
        } else if (value.length < 6) {
          // Don't show error until user has entered 6 digits
          return '';
        } else if (!validatePinCode(value)) {
          return 'Please enter a valid 6-digit PIN code';
        }
        break;
      case 'workAddress.address':
        if (!value || value.trim().length < 10 || value.trim().length > 512) {
          return 'Work address is required and must be at least 10 characters';
        }
        break;
      case 'workAddress.city':
        if (!value || value.trim().length < 2) {
          return 'Work city is required';
        }
        break;
      case 'workAddress.state':
        if (!value || value.trim().length < 2) {
          return 'Work state is required';
        }
        break;
      case 'workAddress.locality':
        if (!value || value.trim().length < 10) {
          return 'Work locality is required and must be at least 5 characters';
        }
        break;
      default:
        break;
    }
    return '';
  };

  // Handle field blur
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors('professional', { ...errors.professional, [name]: error });
    }
  };

  // Handle next button click
  const handleNext = () => {
    console.log('Submit Application clicked');
    console.log('Current professional details:', professionalDetails);
    console.log('Current errors:', errors.professional);
    
    // Validate all required fields
    const requiredFields = ['employmentType', 'companyName', 'jobFunction', 'designation', 'workSector', 'organization', 'workingSince', 'salaryReceivedType', 'netMonthlyIncome'];
    const newErrors = {};
    
    requiredFields.forEach(field => {
      const error = validateField(field, professionalDetails[field]);
      if (error) {
        newErrors[field] = error;
        console.log(`Validation error for ${field}:`, error);
      }
    });

    // Validate optional fields if they have values
    if (professionalDetails.officialEmail) {
      const emailError = validateField('officialEmail', professionalDetails.officialEmail);
      if (emailError) {
        newErrors.officialEmail = emailError;
        console.log('Official email validation error:', emailError);
      }
    }

    // Validate workAddress fields - always validate required fields
    if (professionalDetails.workAddress) {
      const workAddress = professionalDetails.workAddress;
      console.log('Work address data:', workAddress);
      
      // Always validate required fields
      const requiredWorkFields = ['pincode', 'address', 'city', 'state', 'locality'];
      requiredWorkFields.forEach(field => {
        const fieldName = `workAddress.${field}`;
        const fieldValue = workAddress[field];
        const fieldError = validateField(fieldName, fieldValue);
        if (fieldError) {
          newErrors[fieldName] = fieldError;
          console.log(`Work address validation error for ${fieldName}:`, fieldError);
        }
      });
    } else {
      console.log('No work address data found');
      newErrors['workAddress'] = 'Work address is required';
    }

    console.log('All validation errors:', newErrors);

    if (Object.keys(newErrors).length > 0) {
      setErrors('professional', newErrors);
      showToast('Please fill all required fields correctly', 'error');
      return;
    }

    console.log('All validations passed, showing confirmation modal');
    // Show confirmation modal first
    setShowConfirmationModal(true);
  };

  const handleConfirmNext = async () => {
    setShowConfirmationModal(false);
    
    // Save professional details
    setIsSaving(true);
    try {
      await saveProfessionalDetails();
      showToast('Professional details saved successfully!', 'success');
      
      // Create customer via API before redirecting
      let casheLoginUrl = null;
      try {
        const createCustomerPayload = {
          reference_Id: leadData.id
        };
        
        console.log('Creating customer with payload:', createCustomerPayload);
        
        const createCustomerResponse = await fetch(`${CREDIT_API_URL}/cashe/create-customer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(createCustomerPayload)
        });
        
        console.log('Create customer API Response status:', createCustomerResponse.status);
        
        if (createCustomerResponse.ok) {
          const createCustomerResult = await createCustomerResponse.json();
          console.log('Create customer API Response:', createCustomerResult);
          
          // Extract casheLoginUrl from response
          if (createCustomerResult.casheLoginUrl) {
            casheLoginUrl = createCustomerResult.casheLoginUrl;
            console.log('Cashe Login URL received:', casheLoginUrl);
          }
          
          showToast('Customer created successfully!', 'success');
        } else {
          const errorResult = await createCustomerResponse.json();
          console.error('Create customer API Error Response:', errorResult);
          showToast(errorResult?.message || 'Failed to create customer', 'error');
        }
      } catch (createCustomerError) {
        console.error('Error creating customer:', createCustomerError);
        showToast('Error creating customer', 'error');
      }
      
      // Prepare application details for Thank You page
      const applicationDetails = {
        applicationId: leadData.id,
        lendingPartner: leadData.eligibility?.lenderName || 'SnapFunds Partner',
        status: 'In Progress',
        submittedAt: Date.now(), // Add timestamp to ensure fresh data
        casheLoginUrl: casheLoginUrl // Include the cashe login URL
      };
      
      console.log('Application details for Thank You page:', applicationDetails);
      
      // Clear saved step from localStorage since application is completed
      if (leadData?.id) {
        localStorage.removeItem(`applicationStep_${leadData.id}`);
        console.log('Cleared saved application step from localStorage');
      }
      
      // Redirect to Thank You page with application details
      try {
        navigate('/application/thank-you', { 
          state: applicationDetails 
        });
        console.log('Navigation to thank you page initiated');
      } catch (navError) {
        console.error('Navigation error:', navError);
        // Fallback: redirect without state
        window.location.href = '/application/thank-you';
      }
    } catch (error) {
      console.error('Error saving professional details:', error);
      showToast(error.message || 'Failed to save professional details', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-fill work address based on PIN code
  const handleWorkPinCodeChange = async (e) => {
    const { value } = e.target;
    handleChange(e); // Call the regular handleChange first
    
    // If PIN code is valid, try to auto-fill address details
    if (validatePinCode(value)) {
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${value}`);
        const data = await response.json();
        console.log('Work address PIN code API response:', data);
        
        if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice) {
          const postOffice = data[0].PostOffice[0];
          const city = postOffice.District || postOffice.Name;
          const state = postOffice.State;
          console.log('Auto-filling work address:', { city, state });
          
          // Update work address city and state using Zustand store
          updateProfessionalDetail('workAddress.city', city);
          updateProfessionalDetail('workAddress.state', state);
          
          // Clear any existing errors for work city and state
          const newErrors = { ...errors.professional };
          delete newErrors['workAddress.city'];
          delete newErrors['workAddress.state'];
          setErrors('professional', newErrors);
          
          showToast(`Work address auto-filled: ${city}, ${state}`, 'success');
        } else {
          console.log('No data found for work PIN code:', value);
        }
      } catch (error) {
        console.error('Error fetching work PIN code data:', error);
        showToast('Unable to auto-fill work address. Please enter manually.', 'warning');
      }
    }
  };

  // Helper function to check if field has data
  const hasFieldData = (fieldName) => {
    // Always show all fields - we want to pre-fill them with API data
    return true;
  };

  // Check if form is valid for enabling/disabling submit button
  const isFormValid = () => {
    // Check if all required fields are filled
    const requiredFields = ['employmentType', 'companyName', 'jobFunction', 'designation', 'workSector', 'organization', 'workingSince', 'salaryReceivedType', 'netMonthlyIncome'];
    const hasAllRequiredFields = requiredFields.every(field => {
      const value = professionalDetails[field];
      return value && value.toString().trim().length > 0;
    });

    // Check if work address fields are filled
    const workAddress = professionalDetails.workAddress;
    const hasWorkAddressFields = workAddress && 
      workAddress.pincode && workAddress.pincode.trim().length > 0 &&
      workAddress.address && workAddress.address.trim().length > 0 &&
      workAddress.city && workAddress.city.trim().length > 0 &&
      workAddress.state && workAddress.state.trim().length > 0 &&
      workAddress.locality && workAddress.locality.trim().length > 0;

    // Check if there are no validation errors (only check for empty string errors, not undefined)
    const hasNoErrors = Object.keys(errors.professional || {}).length === 0 || 
      Object.values(errors.professional || {}).every(error => !error || error.trim() === '');

    // Validate optional email if provided
    let hasValidEmail = true;
    if (professionalDetails.officialEmail && professionalDetails.officialEmail.trim().length > 0) {
      hasValidEmail = validateEmail(professionalDetails.officialEmail);
    }

    // Debug logging
    console.log('Form validation debug:', {
      hasAllRequiredFields,
      hasWorkAddressFields,
      hasNoErrors,
      hasValidEmail,
      errors: errors.professional,
      professionalDetails,
      workAddress
    });

    return hasAllRequiredFields && hasWorkAddressFields && hasNoErrors && hasValidEmail;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="onboard-step">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div className="mini-spinner" style={{ margin: '0 auto 20px' }}></div>
          <p>Loading your professional details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="onboard-step">
      <h3 style={{ marginBottom: '20px', color: '#283782' }}>Your professional details will help in completing your application further!</h3>
      
      <Form id="professional-details-form">
        {/* Employment Type and Company Name */}
        <div className="input-row">
          {hasFieldData('employmentType') && (
            <div className={`floating-input-group ${errors.professional.employmentType ? 'error' : ''}`}>
              <select
                name="employmentType"
                id="employmentType"
                value={professionalDetails.employmentType}
                onChange={handleChange}
                onBlur={handleBlur}
                className="floating-select"
                disabled
                style={{ backgroundColor: '#f8f9fa', color: '#6c757d', cursor: 'not-allowed' }}
              >
                <option value="">Select Employment Type</option>
                <option value="Salaried">Salaried</option>
                <option value="SelfEmployed">Self Employed</option>
                <option value="Business">Business</option>
                <option value="Other">Other</option>
              </select>
              <label htmlFor="employmentType" className="floating-label">Select Your Employment Type</label>
              {errors.professional.employmentType && (
                <div className="field-error">{errors.professional.employmentType}</div>
              )}
            </div>
          )}

          {hasFieldData('companyName') && (
            <div className={`floating-input-group ${errors.professional.companyName ? 'error' : ''}`}>
              <input
                type="text"
                name="companyName"
                id="companyName"
                value={professionalDetails.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
                className="floating-input"
                placeholder=" "
              />
              <label htmlFor="companyName" className="floating-label">Enter Your Company Name</label>
              {errors.professional.companyName && (
                <div className="field-error">{errors.professional.companyName}</div>
              )}
            </div>
          )}
        </div>

        {/* Job Function and Designation */}
        <div className="input-row">
          {hasFieldData('jobFunction') && (
            <div className={`floating-input-group ${errors.professional.jobFunction ? 'error' : ''}`}>
              <select
                name="jobFunction"
                id="jobFunction"
                value={professionalDetails.jobFunction}
                onChange={handleChange}
                onBlur={handleBlur}
                className="floating-select"
              >
                <option value="">Select Job Function</option>
                <option value="Research and development (R & D)">Research and Development (R&D)</option>
                <option value="Administration">Administration</option>
                <option value="Customer service">Customer Service</option>
                <option value="Sales">Sales</option>
                <option value="Marketing">Marketing</option>
                <option value="IT and Engineering">IT and Engineering</option>
                <option value="Human resources">Human Resources</option>
                <option value="Finance">Finance</option>
                <option value="Operations">Operations</option>
                <option value="Legal and Audit">Legal and Audit</option>
                <option value="Others">Others</option>
              </select>
              <label htmlFor="jobFunction" className="floating-label">Select Your Job Function</label>
              {errors.professional.jobFunction && (
                <div className="field-error">{errors.professional.jobFunction}</div>
              )}
            </div>
          )}

          {hasFieldData('designation') && (
            <div className={`floating-input-group ${errors.professional.designation ? 'error' : ''}`}>
              <select
                name="designation"
                id="designation"
                value={professionalDetails.designation}
                onChange={handleChange}
                onBlur={handleBlur}
                className="floating-select"
              >
                <option value="">Select Designation</option>
                <option value="Executive">Executive</option>
                <option value="Middle Management">Middle Management</option>
                <option value="Sr.Management">Sr.Management</option>
                <option value="Leadership role">Leadership role</option>
              </select>
              <label htmlFor="designation" className="floating-label">Select Your Designation</label>
              {errors.professional.designation && (
                <div className="field-error">{errors.professional.designation}</div>
              )}
            </div>
          )}
        </div>

        {/* Work Sector and Organization */}
        <div className="input-row">
          {hasFieldData('workSector') && (
            <div className={`floating-input-group ${errors.professional.workSector ? 'error' : ''}`}>
              <select
                name="workSector"
                id="workSector"
                value={professionalDetails.workSector}
                onChange={handleChange}
                onBlur={handleBlur}
                className="floating-select"
              >
                <option value="">Select Work Sector</option>
                <option value="Agriculture">Agriculture</option>
                <option value="Banking and Financial Services">Banking and Financial Services</option>
                <option value="Construction, Real Estate and Infrastructure">Construction, Real Estate and Infrastructure</option>
                <option value="Education and Training">Education and Training</option>
                <option value="Government and Public Administration">Government and Public Administration</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Information Technology">Information Technology</option>
                <option value="Legal">Legal</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Media, Sports and Entertainment">Media, Sports and Entertainment</option>
                <option value="Oil and Gases">Oil and Gases</option>
                <option value="Security and Law Enforcement">Security and Law Enforcement</option>
                <option value="Trade and Commerce">Trade and Commerce</option>
                <option value="Transport and Logistics">Transport and Logistics</option>
                <option value="Tourism and Hospitality">Tourism and Hospitality</option>
                <option value="Telecommunication">Telecommunication</option>
                <option value="Others">Others</option>
              </select>
              <label htmlFor="workSector" className="floating-label">Select Your Work Sector</label>
              {errors.professional.workSector && (
                <div className="field-error">{errors.professional.workSector}</div>
              )}
            </div>
          )}

          {hasFieldData('organization') && (
            <div className={`floating-input-group ${errors.professional.organization ? 'error' : ''}`}>
              <select
                name="organization"
                id="organization"
                value={professionalDetails.organization}
                onChange={handleChange}
                onBlur={handleBlur}
                className="floating-select"
              >
                <option value="">Select Organization Type</option>
                <option value="Private Company">Private Company</option>
                <option value="Public Company">Public Company</option>
                <option value="Listed Public Company">Listed Public Company</option>
                <option value="State Government">State Government</option>
                <option value="Central Government">Central Government</option>
                <option value="Partnership Firm">Partnership Firm</option>
                <option value="Proprietorship Firm">Proprietorship Firm</option>
                <option value="Limited Liability Partnership (LLP)">Limited Liability Partnership (LLP)</option>
                <option value="Trust">Trust</option>
              </select>
              <label htmlFor="organization" className="floating-label">Select Your Organization Type</label>
              {errors.professional.organization && (
                <div className="field-error">{errors.professional.organization}</div>
              )}
            </div>
          )}
        </div>

        {/* Working Since and Official Email */}
        <div className="input-row">
          {hasFieldData('workingSince') && (
            <div className={`floating-input-group ${errors.professional.workingSince ? 'error' : ''}`}>
              <BootstrapDatePicker
                name="workingSince"
                id="workingSince"
                value={professionalDetails.workingSince}
                onChange={handleChange}
                placeholder="Working Since"
                maxDate={new Date().toISOString().split('T')[0]}
                error={errors.professional.workingSince}
                className="professional-datepicker"
                yearRange="unrestricted"
              />
              {errors.professional.workingSince && (
                <div className="field-error">{errors.professional.workingSince}</div>
              )}
            </div>
          )}

          {hasFieldData('officialEmail') && (
            <div className={`floating-input-group ${errors.professional.officialEmail ? 'error' : ''}`}>
              <input
                type="email"
                name="officialEmail"
                id="officialEmail"
                value={professionalDetails.officialEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                className="floating-input"
                placeholder=" "
              />
              <label htmlFor="officialEmail" className="floating-label">Enter Your Official Email (Optional)</label>
              {errors.professional.officialEmail && (
                <div className="field-error">{errors.professional.officialEmail}</div>
              )}
            </div>
          )}
        </div>

        {/* Salary Received Type and Net Monthly Income */}
        <div className="input-row">
          {hasFieldData('salaryReceivedType') && (
            <div className={`floating-input-group ${errors.professional.salaryReceivedType ? 'error' : ''}`}>
              <select
                name="salaryReceivedType"
                id="salaryReceivedType"
                value={professionalDetails.salaryReceivedType}
                onChange={handleChange}
                onBlur={handleBlur}
                className="floating-select"
                disabled
                style={{ backgroundColor: '#f8f9fa', color: '#6c757d', cursor: 'not-allowed' }}
              >
                <option value="">Select Salary Received Type</option>
                <option value="Direct Bank Account">Direct Bank Account</option>
                <option value="Cheque">Cheque</option>
                <option value="Cash">Cash</option>
              </select>
              <label htmlFor="salaryReceivedType" className="floating-label">Select Your Salary Received Type</label>
              {errors.professional.salaryReceivedType && (
                <div className="field-error">{errors.professional.salaryReceivedType}</div>
              )}
            </div>
          )}

          {hasFieldData('netMonthlyIncome') && (
            <div className={`floating-input-group ${errors.professional.netMonthlyIncome ? 'error' : ''}`}>
              <input
                type="number"
                name="netMonthlyIncome"
                id="netMonthlyIncome"
                value={professionalDetails.netMonthlyIncome}
                onChange={handleChange}
                onBlur={handleBlur}
                className="floating-input"
                placeholder=" "
                min="5000"
                disabled
                style={{ backgroundColor: '#f8f9fa', color: '#6c757d', cursor: 'not-allowed' }}
              />
              <label htmlFor="netMonthlyIncome" className="floating-label">Enter Your Net Monthly Income (₹)</label>
              {errors.professional.netMonthlyIncome && (
                <div className="field-error">{errors.professional.netMonthlyIncome}</div>
              )}
            </div>
          )}
        </div>

        {/* Work Address Section */}
        <div>
          <h4 style={{ marginTop: '32px', marginBottom: '16px', color: '#283782' }}>Office Address</h4>
          
          {/* Work PIN Code and Address */}
          <div className="input-row">
            {hasFieldData('workPincode') && (
              <div className={`floating-input-group ${errors.professional['workAddress.pincode'] ? 'error' : ''}`} style={{ position: 'relative' }}>
                <input
                  ref={workPincodeAutocomplete.inputRef}
                  type="text"
                  name="workAddress.pincode"
                  id="workPincode"
                  value={professionalDetails.workAddress?.pincode || ''}
                  onChange={(e) => {
                    const filteredValue = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                    const currentWorkAddress = professionalDetails.workAddress || {};
                    const updatedWorkAddress = {
                      ...currentWorkAddress,
                      pincode: filteredValue
                    };
                    updateProfessionalDetail('workAddress', updatedWorkAddress);
                    
                    // Clear error when user starts typing
                    if (errors.professional['workAddress.pincode']) {
                      setErrors('professional', { ...errors.professional, 'workAddress.pincode': '' });
                    }
                    
                    // Validate field
                    const error = validateField('workAddress.pincode', filteredValue);
                    if (error) {
                      setErrors('professional', { ...errors.professional, 'workAddress.pincode': error });
                    }
                    
                    // Trigger debounced search for autocomplete
                    workPincodeAutocomplete.debouncedSearch(filteredValue);
                  }}
                  onFocus={() => {
                    if (workPincodeAutocomplete.suggestions.length > 0) {
                      workPincodeAutocomplete.setShowDropdown(true);
                    }
                  }}
                  onBlur={handleBlur}
                  className="floating-input"
                  placeholder=" "
                  inputMode="numeric"
                  maxLength={6}
                />
                <label htmlFor="workPincode" className="floating-label">PIN Code</label>
                
                {/* Pincode Suggestions Dropdown */}
                <AutocompleteDropdown
                  suggestions={workPincodeAutocomplete.suggestions}
                  showDropdown={workPincodeAutocomplete.showDropdown}
                  isLoading={workPincodeAutocomplete.isLoading}
                  onSelect={workPincodeAutocomplete.handleSelect}
                  getValue={workPincodeAutocomplete.getValue}
                  getKey={workPincodeAutocomplete.getKey}
                  dropdownRef={workPincodeAutocomplete.dropdownRef}
                />
                
                {errors.professional['workAddress.pincode'] && (
                  <div className="field-error">{errors.professional['workAddress.pincode']}</div>
                )}
              </div>
            )}
            {hasFieldData('workState') && (
              <div className={`floating-input-group ${errors.professional['workAddress.state'] ? 'error' : ''}`}>
                <input
                  type="text"
                  name="workAddress.state"
                  id="workState"
                  value={professionalDetails.workAddress?.state || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="floating-input"
                  placeholder=" "
                />
                <label htmlFor="workState" className="floating-label">State</label>
                {errors.professional['workAddress.state'] && (
                  <div className="field-error">{errors.professional['workAddress.state']}</div>
                )}
              </div>
            )}
          </div>

           
          

          {/* Work Locality and City */}
          <div className="input-row">

          {hasFieldData('workCity') && (
              <div className={`floating-input-group ${errors.professional['workAddress.city'] ? 'error' : ''}`}>
                <input
                  type="text"
                  name="workAddress.city"
                  id="workCity"
                  value={professionalDetails.workAddress?.city || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="floating-input"
                  placeholder=" "
                />
                <label htmlFor="workCity" className="floating-label">City</label>
                {errors.professional['workAddress.city'] && (
                  <div className="field-error">{errors.professional['workAddress.city']}</div>
                )}
              </div>
            )}
          {hasFieldData('workAddress') && (
              <div className={`floating-input-group ${errors.professional.workAddress ? 'error' : ''}`}>
                <input
                  type="text"
                  name="workAddress.address"
                  id="workAddress"
                  value={professionalDetails.workAddress?.address || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="floating-input"
                  placeholder=" "
                />
                <label htmlFor="workAddress" className="floating-label">Address</label>
                {errors.professional['workAddress.address'] && (
                  <div className="field-error">{errors.professional['workAddress.address']}</div>
                )}
              </div>
            )}
         

            
          </div>

          {/* Work State */}
          <div className="input-row">
          {hasFieldData('workLocality') && (
              <div className={`floating-input-group ${errors.professional['workAddress.locality'] ? 'error' : ''}`}>
                <input
                  type="text"
                  name="workAddress.locality"
                  id="workLocality"
                  value={professionalDetails.workAddress?.locality || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="floating-input"
                  placeholder=" "
                />
                <label htmlFor="workLocality" className="floating-label">Enter Your Work Locality</label>
                {errors.professional['workAddress.locality'] && (
                  <div className="field-error">{errors.professional['workAddress.locality']}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </Form>

      {/* Navigation Buttons */}
      <div className="wizard-nav">
        <button className="btn-onboard-next" onClick={handleNext} disabled={isSubmitting || isSaving || !isFormValid()}>
          {isSaving ? 'Saving...' : 'Submit Application'} <i className="fas fa-arrow-right"></i>
        </button>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showConfirmationModal}
        onConfirm={handleConfirmNext}
        onCancel={() => setShowConfirmationModal(false)}
        isLoading={isSaving}
      />
    </div>
  );
};

export default ProfessionalDetailsStep;